// Floor-plan mode: tool state, gestures (draw/select/marquee/openings),
// snapping, in-place dimension editing, plan save/load, floor undo/redo,
// opening handles, and the floor transform gizmo.
//
// Extracted from main.ts. All dependencies on the host app come in through
// the FloorModeCtx; everything main.ts needs back is in the returned API.
import * as THREE from "three";
import { OrbitControls } from "three/addons/controls/OrbitControls.js";

import { Orbit3D } from "../view/orbit3d";
import {
  type PaletteLookup,
} from "../view/voxel-mesh";
import {
  consumeDesktopPress,
  consumeDesktopRelease,
  desktopMouse,
} from "../input/desktop";
import { elementRect } from "../input/element-rect";
import { uiState } from "../ui/state";
import {
  M_TO_IN,
  formatLengthIn,
  getDisplayUnit,
  parseLength,
  setDisplayUnit,
} from "../model/units";
import { getModel } from "../model/collection-store";
import {
  type SavedModel,
} from "../model/saved-model";
import { type CollectionDock } from "../ui/collection-dock";
import {
  expandRigidGroup,
  extraDims,
  floorScene,
  floorTools,
  measureSystem,
  placementSystem,
  setMeasureToolActive,
  wallLabels,
  wallSystem,
} from "./core";
import {
  type OpeningKind,
  WALL_HEIGHT,
  WALL_THICKNESS,
  WallSystem,
} from "./walls";
import {
  cloneForModel,
  dropClone,
  ensureAO,
  ensureAsset,
  hasAO,
  hasAsset,
} from "./bake-cache";
import { beginBusy } from "../ui/busy";
import { PlacementSystem } from "./placement";
import { type MeasureComp, type SerMeasurePt } from "./measure";

import { type DimSpec, DimensionLabels } from "./dimension-labels";
import { type SavedFloorPlan, type SavedPlacement } from "./saved-floor-plan";
import { newPlanId, putPlan, readPlanFile } from "./floor-plan-store";
import { type FloorTool } from "../ui/floor-ui";
import { installMeasureTool } from "./tools/measure";
import {
  type OpeningField,
  installOpeningsDomain,
  sameOpening as _sameOpening,
} from "./tools/openings";
import { installWallsDomain } from "./tools/walls";
import { installMarquee } from "./tools/marquee";
import { installPlacementsDomain } from "./tools/placements";
import {
  commitFloor,
  floorDirty,
  floorRedoAction,
  floorUndoAction,
  initFloorHistory,
  markFloorClean,
  markFloorMutation,
  resetFloorHistory,
} from "./history";
import { type SectionRegistry } from "../ui/section-registry";
import { PlacementOutline } from "../view/placement-outline";
import { showAlert } from "../ui/modal";
import {
  type ContextMenuItem,
  closeContextMenu,
  isContextMenuOpen,
  showContextMenu,
} from "../ui/context-menu";
import { type SaveSession } from "../ui/save-session";
import { type LifecycleEvent } from "../app/lifecycle";
import { type installFloorUI as InstallFloorUIFn } from "../ui/floor-ui";
import { type installDesktopMarqueeRect as InstallMarqueeRectFn } from "../input/desktop";

// Everything floor mode needs from the host app. The UI shell, save session,
// and collection dock are created after installFloorMode() runs, so they come
// in as thunks rather than values.
export interface FloorModeCtx {
  scene: THREE.Scene;
  renderer: THREE.WebGLRenderer;
  palette: PaletteLookup;
  orbitControls: Orbit3D;
  orthoCamera: THREE.OrthographicCamera;
  floor2DControls: OrbitControls;
  desktopMarqueeRect: ReturnType<typeof InstallMarqueeRectFn>;
  activeCamera: () => THREE.Camera;
  frameFloorView: (center?: THREE.Vector3) => void;
  pickFile: (accept: string, cb: (file: File) => void) => void;
  refreshFloorSaveUI: () => void;
  wallsOn: () => boolean;
  wallDimsOn: () => boolean;
  modelDimsOn: () => boolean;
  wakeRendering: () => void; // re-wake the idle-gated render loop after async work
  emitLifecycle: (event: LifecycleEvent) => void; // fire an app lifecycle event
  floorUI: () => ReturnType<typeof InstallFloorUIFn>;
  sections: SectionRegistry; // sidebar sections (floor-ui mounts; slices register)
  floorSession: () => SaveSession;
  modelDock: () => CollectionDock | null;
}

export function installFloorMode(ctx: FloorModeCtx) {
  const {
    scene,
    renderer,
    palette,
    orbitControls,
    orthoCamera,
    floor2DControls,
    desktopMarqueeRect,
    activeCamera,
    frameFloorView,
    pickFile,
    refreshFloorSaveUI,
    wakeRendering,
  } = ctx;

// ── Floor-planning mode (continuous) ───────────────────────────────────────
// The scene root + systems (wallSystem / placementSystem / measureSystem /
// wallLabels / extraDims) are owned by floor/core — imported above so future
// floor slices can share them. Only the scene attachment happens here.
scene.add(floorScene.root);
// Glowing rim around the selected placement's mesh (attaches to its group).
// (The transform gizmo + rotation protractor live in floor/tools/placements.)
const placementOutline = new PlacementOutline();

// ── Opening handles + dimension editor ──────────────────────────────────────
// (The opening visuals — editor group, pills, resize handles, border — live in
// floor/tools/openings now, parented to floorScene.root by the slice.)

// Wall-face pick occluder for the 2D SIDE-ELEVATION view. Looking at a wall
// head-on you see the room THROUGH its doorway/windows; their meshes are holes,
// and several pick paths (model hover, right-click, measurement + dim pills)
// have no wall occlusion at all — so room content on the far side stays
// hoverable/clickable through the opening. This returns the nearest distance
// along _floorRay where it crosses ANY wall's full face (openings treated as
// SOLID, plus a margin above the top), so callers can reject picks beyond it.
// Infinity in 3D / top-down (real depth-testing / no "through a wall" there) and
// when there are no walls. _floorRay.ray.direction is normalised, so the plane
// parameter t is a world distance directly comparable to raycaster hit.distance.
function wallFaceOccluderDist(): number {
  if (!inFloorElevation()) return Infinity;
  const o = _floorRay.ray.origin;
  const d = _floorRay.ray.direction;
  const topY = wallSystem.height() + 3; // wall face + a margin above its top
  let best = Infinity;
  for (const w of wallSystem.wallSegments()) {
    const ex = w.bx - w.ax;
    const ez = w.bz - w.az;
    const L = Math.hypot(ex, ez);
    if (L < 1e-6) continue;
    const ux = ex / L;
    const uz = ez / L;
    const nx = -uz; // wall's horizontal normal
    const nz = ux;
    const dn = d.x * nx + d.z * nz;
    if (Math.abs(dn) < 1e-6) continue; // ray parallel to this wall → no crossing
    const t = -((o.x - w.ax) * nx + (o.z - w.az) * nz) / dn;
    if (t <= 0 || t >= best) continue;
    const s = (o.x + d.x * t - w.ax) * ux + (o.z + d.z * t - w.az) * uz;
    if (s < 0 || s > L) continue; // crossing is off the ends of the wall
    const hy = o.y + d.y * t;
    if (hy < -0.5 || hy > topY) continue; // above/below the wall face
    best = t;
  }
  return best;
}
// Map a placement between its runtime id (live) and its index in iteration order
// (stable across save/undo, which mint fresh ids) — for serializing anchors.
const measureAnchorToIndex = (id: number): number => placementSystem.ids().indexOf(id);
const measureAnchorToId = (i: number): number | null => {
  const all = placementSystem.ids();
  return i >= 0 && i < all.length ? all[i] : null;
};


// ── Floor tools (Phase 1: walls) ───────────────────────────────────────────
let floorTool: FloorTool = "select";
// (Wall-draw + corner press/drag state lives in floor/tools/walls now.)
// In-place corner lock (Select tool): the corner under the press, and whether
// the gesture became a drag. A click (no drag) toggles the corner's lock; a drag
// moves it.
// Measurement vertex (Select tool) — same click-toggles-lock / drag-moves
// pattern as a wall corner, but for a measurement endpoint node.
// (Measurement-vertex press/drag state lives in floor/tools/measure now.)
// (Wall pill/body drag state lives in floor/tools/walls now.)
// Dragging a wall's body to translate it. grab* is the press point; applied* is
// the total (world) delta moved so far — motion is locked to the wall's own
// parallel/perpendicular axis and grid-snapped (Shift = free 2D), and applying
// only the per-frame change keeps it drift-free.
// (Opening selection/hover/gesture state lives in floor/tools/openings now —
// read through the `openings` domain API installed below.)
let shiftHeld = false;

// ── Marquee multi-selection (layout Select tool) ────────────────────────────
// (The marquee domain — sets, rubber-band gesture, screen-space hit tests,
// front-layer filter, delete-selected — lives in floor/tools/marquee. The group
// MOVE below stays here with the gizmo/placement-move cluster.)
// (The marquee group move lives in floor/tools/placements.)

const FLOOR_GRID = 0.05; // 5 cm position snap
const FLOOR_ANGLE = Math.PI / 12; // 15° angle snap
const FLOOR_AXIS_SNAP = (5 * Math.PI) / 180; // corner drag: within 5° of an axis → square it
// Wall-body move: only LOCK to the wall's parallel/perpendicular axis when the
// drag is within this angle of it; wider drags move freely. tan precomputed.
const WALL_MOVE_SNAP_TAN = Math.tan((15 * Math.PI) / 180); // ≈0.268 (15°)

const _floorRay = new THREE.Raycaster();
const _floorPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
const _floorHit = new THREE.Vector3();
function floorPointAtCursor(): { x: number; z: number } | null {
  // The floor-plane tools (placement/group/wall/corner drag, wall draw, measure)
  // are top-down operations that read this point. In a 2D side elevation the view
  // ray runs nearly parallel to the floor, so the intersection shoots off to a
  // garbage far point — return null there so those tools no-op instead of flinging
  // things to absurd coords. (Object manipulation from the side is the gizmo's job.)
  if (uiState.floorView === "2d" && planViewAxis() !== 1) return null;
  _floorRay.setFromCamera(desktopMouse.ndc, activeCamera());
  if (_floorRay.ray.intersectPlane(_floorPlane, _floorHit)) {
    return { x: _floorHit.x, z: _floorHit.z };
  }
  return null;
}
// (viewPlanePointAtCursor lives in floor/tools/placements.)
function snapToGrid(v: number): number {
  return Math.round(v / FLOOR_GRID) * FLOOR_GRID;
}
// Closest point on segment A→B to p, plus the distance to it.
// (closestOnSeg lives in floor/geom now.)
// (Wall-draw snapping — snapPoint/snapSegmentEnd/alignWallEnd — lives in
// floor/tools/walls.)

// ── Placement snapping ──────────────────────────────────────────────────────
// (PLACE_SNAP / snapFootprint / snapGroup live in floor/tools/placements.)

// The one wall the opening-add UI (+ Door / + Window) applies to: a plain single
// wall selection, OR a marquee that's exactly one wall and no placements. Shift-
// clicking a lone wall folds it into the marquee and clears the plain selection,
// but it's still "one wall" — so the buttons shouldn't vanish.
function soleSelectedWall(): number | null {
  const wid = wallSystem.selectedId();
  if (wid !== null) return wid;
  if (marquee.places().size === 0 && marquee.walls().size === 1) return [...marquee.walls()][0];
  return null;
}

function refreshFloorSelectionUI(): void {
  const wid = soleSelectedWall();
  const pid = placementSystem.selectedId();
  // The Flip / Switch-sides buttons follow the door selection (mutually
  // exclusive with the wall/placement selections handled below).
  ctx.floorUI().showDoorSelection(hasSelectedDoor());
  if (wid !== null) {
    ctx.floorUI().showSelection({ openings: wallSystem.openingsOf(wid) });
    ctx.floorUI().showPlacementSelection(null);
  } else if (pid !== null) {
    const info = placementSystem.info(pid);
    const size = placementSystem.sizeOf(pid);
    const fmt = (m: number) => formatLengthIn(m * M_TO_IN, getDisplayUnit());
    ctx.floorUI().showSelection(null);
    ctx.floorUI().showPlacementSelection(
      info
        ? {
            rotationDeg: info.rotationDeg,
            scale: info.scale,
            dims: size
              ? `${fmt(size.w)} × ${fmt(size.d)} × ${fmt(size.h)}`
              : "",
          }
        : null,
    );
  } else {
    ctx.floorUI().showSelection(null);
    ctx.floorUI().showPlacementSelection(null);
  }
}

// Selecting a wall or a placement clears the other (and any marquee selection).
function selectWall(id: number | null): void {
  clearMarquee();
  wallSystem.setSelected(id);
  if (id !== null) {
    placementSystem.setSelected(null);
    clearMeasureSelection(); // selecting anything else drops the measurement highlight
    clearOpeningSelection(); // ...and the door/window editor — one selection at a time
  }
  refreshFloorSelectionUI();
}
function selectPlacement(id: number | null): void {
  clearMarquee();
  placementSystem.setSelected(id);
  if (id !== null) {
    wallSystem.setSelected(null);
    clearMeasureSelection();
    clearOpeningSelection();
  }
  refreshFloorSelectionUI();
}
// Select a placement; if it belongs to a GROUP, select the whole group instead
// (so the group highlights and moves/rotates as a unit). A group selection rides
// on the marquee set, reusing all the multi-select move/rotate/delete machinery.
function selectPlacementOrGroup(id: number): void {
  const sibs = placementSystem.groupSiblings(id);
  if (sibs.length <= 1) {
    selectPlacement(id);
    return;
  }
  placementSystem.setSelected(null);
  wallSystem.setSelected(null);
  clearMeasureSelection();
  clearOpeningSelection();
  marquee.setSelection([], sibs);
  refreshFloorSelectionUI();
}
// The currently-selected placements: the marquee set, or the single gizmo
// selection, or none.
function selectedPlacementIds(): number[] {
  if (marquee.places().size > 0) return [...marquee.places()];
  const sid = placementSystem.selectedId();
  return sid !== null ? [sid] : [];
}
// Make the marquee set exactly `ids` and highlight them (used after group /
// duplicate so the result reads as one selected unit).
function selectPlacements(ids: Iterable<number>): void {
  placementSystem.setSelected(null);
  wallSystem.setSelected(null);
  clearMeasureSelection();
  clearOpeningSelection();
  marquee.setSelection([], ids);
  refreshFloorSelectionUI();
}
// The context-menu / group operations act on an explicit id set (the right-click
// target), NOT on the selection — right-clicking never selects anything.
//
// Group placements (needs ≥2) into one unit. Doesn't change what's selected.
function groupPlacements(ids: number[]): void {
  if (ids.length < 2) return;
  if (placementSystem.createGroup(ids) === null) return;
  commitFloor();
  refreshFloorSelectionUI();
}
// Ungroup the group(s) any of `ids` belongs to. Doesn't change what's selected.
function ungroupPlacements(ids: number[]): void {
  if (ids.length === 0) return;
  placementSystem.ungroup(ids);
  commitFloor();
  refreshFloorSelectionUI();
}
// Whether `ids` contains a grouped model (→ offer Ungroup).
function idsHaveGroup(ids: number[]): boolean {
  return ids.some((id) => placementSystem.isGrouped(id));
}
// Whether `ids` is already exactly one whole group (→ no "Group" item).
function idsAreSingleGroup(ids: number[]): boolean {
  if (ids.length < 2 || !placementSystem.isGrouped(ids[0])) return false;
  const sibs = new Set(placementSystem.groupSiblings(ids[0]));
  return ids.length === sibs.size && ids.every((id) => sibs.has(id));
}
// Whether a single model's dimension lines are effectively visible: its own
// override if set, otherwise the global "Model dimensions" toggle. (Elevation
// view also suppresses them, but that's transient and not what the menu acts on.)
function dimsVisibleOf(id: number): boolean {
  return placementSystem.dimsOverrideOf(id) ?? ctx.modelDimsOn();
}
// Show/hide dimension lines for JUST `ids` (a group toggles together) by setting
// their per-model override — independent of the global toggle and of every other
// model. No selection change.
function toggleDims(ids: number[]): void {
  if (ids.length === 0) return;
  placementSystem.setDimsOverride(ids, !idsDimsShown(ids));
  commitFloor();
}
// Whether `ids`' dimensions are currently visible — drives the menu label so it
// offers "Show" exactly when they're hidden and "Hide" when they're showing.
function idsDimsShown(ids: number[]): boolean {
  return ids.some((id) => dimsVisibleOf(id));
}
// Whether any of `ids` is currently shown — drives the Hide/Show model label.
function idsModelShown(ids: number[]): boolean {
  return ids.some((id) => !placementSystem.isHidden(id));
}
// Hide (or show) JUST `ids` via their per-model hide flag — independent of the
// global "Models" toggle. Hiding clears the placement selection (a hidden model
// can't keep a floating gizmo). One undo step.
function toggleModelHidden(ids: number[]): void {
  if (ids.length === 0) return;
  const hiding = idsModelShown(ids);
  placementSystem.setHidden(ids, hiding);
  if (hiding) {
    placementSystem.setSelected(null);
    clearMarquee();
  }
  refreshFloorSelectionUI();
  commitFloor();
}

// Drop every kind of floor selection: wall, placement, marquee, corner padlock,
// and any open dimension editor. Used by Escape and clicking empty space.
function clearFloorSelection(): void {
  closeDimEdit();
  selectFloorVertex(null);
  clearOpeningSelection();
  wallSystem.setSelected(null);
  placementSystem.setSelected(null);
  clearMeasureSelection();
  clearMarquee();
  refreshFloorSelectionUI();
}

// Drop the measurement segment-line (blue) selection. (Vertices no longer have a
// persistent selected state — a click just toggles their lock.)
function clearMeasureSelection(): void {
  measureSystem.setSelected(null);
}

// (finishDraw lives in floor/tools/walls; aliased below.)

// Force-cancel every in-flight pointer gesture — all the drags, the marquee
// rubber band, the wall-polyline preview, and corner-press tracking — without
// the release handler's commit/click semantics and without touching what's
// selected. This is the ONE teardown that tool switches, mode switches, and
// view switches share, so their reset lists can't drift apart (the lists in
// setMode and setFloorTool had, before this).
function cancelFloorGesture(): void {
  finishDraw(); // drop the in-progress polyline preview
  walls.cancelCornerGesture();
  cancelMeasureVertexGesture();
  walls.clearWallGestures();
  openings.clearGesture();
  placements.clearGestures();
  floorProtractor.hide(); // only shown mid rotate-drag
  marquee.cancelBand(); // drop the in-flight rubber band (no release semantics)
  desktopMarqueeRect.hide();
}

function setFloorTool(t: FloorTool): void {
  floorTool = t;
  cancelFloorGesture();
  closeDimEdit(); // a tool switch dismisses any open in-place dimension editor
  selectFloorVertex(null); // and any in-place corner-lock padlock
  clearOpeningSelection();
  clearMeasureSelection();
  clearMarquee();
  measureSystem.finish(); // end the current chain; it persists, next click starts fresh
  setMeasureToolActive(t === "measure"); // auto-show while measuring; the "Measurements" toggle can also pin them on
  if (t !== "select") {
    // Only the Select tool keeps a wall/placement selection + gizmo.
    wallSystem.setSelected(null);
    placementSystem.setSelected(null);
    refreshFloorSelectionUI();
  }
  ctx.floorUI().render();
}

function setWallLengthFromText(id: number, text: string): void {
  // In ft/in mode the length reads as feet+inches (e.g. 6' 6"), so a bare
  // number should mean FEET, not inches. Explicit units (48", 1.5m) still win.
  const unit = getDisplayUnit();
  const defaultUnit = unit === "in" ? "ft" : unit;
  const inches = parseLength(text, defaultUnit);
  if (inches === null || inches <= 0) return;
  // Sets the length on the wall's chosen dimension side (centerline or a face),
  // matching what the pill + this field display.
  wallSystem.setWallReferenceLength(id, inches * 0.0254);
  refreshFloorSelectionUI();
  commitFloor();
}
// Set the uniform room (wall) height from the toolbar field. Same parse rules as
// the wall length field: a bare number is feet in ft/in mode, metres otherwise.
function setRoomHeight(text: string): void {
  const unit = getDisplayUnit();
  const inches = parseLength(text, unit === "in" ? "ft" : unit);
  if (inches === null || inches <= 0) {
    ctx.floorUI().render(); // reject (e.g. empty) → restore the displayed value
    return;
  }
  wallSystem.setHeight(inches * 0.0254);
  commitFloor(); // one undo step; also re-renders the field
  ctx.floorUI().render();
}

// ── In-place dimension editing ──────────────────────────────────────────────
// Click a wall's dimension pill (floor Select tool) to type a new length right
// on the plan — a DOM input floated over the pill, mirroring the toolbar's
// length field (same parse rules, same target: the wall's current dim side).
const DIM_EDIT_FONT = "600 12px system-ui,-apple-system,sans-serif";
const dimEdit = document.createElement("input");
dimEdit.type = "text";
dimEdit.id = "fp-dim-edit";
dimEdit.autocomplete = "off";
dimEdit.style.cssText =
  "position:fixed; display:none; z-index:30; box-sizing:border-box;" +
  "transform:translate(-50%,-50%); text-align:center; padding:3px 4px;" +
  `font:${DIM_EDIT_FONT}; color:#11141c;` +
  "background:#ffe87a; border:1px solid #caa400; border-radius:6px; outline:none;" +
  "box-shadow:0 2px 8px rgba(0,0,0,0.35);";
document.body.appendChild(dimEdit);
// Off-screen twin used to measure the text so the box can size to its content.
const dimEditMeasure = document.createElement("span");
dimEditMeasure.style.cssText =
  `position:absolute; left:-9999px; top:-9999px; visibility:hidden;` +
  `white-space:pre; font:${DIM_EDIT_FONT};`;
document.body.appendChild(dimEditMeasure);
// The in-place dimension editor targets either a wall's length or one of a
// door/window's editable fields (size or a gap to a neighbour / wall extent).
type DimEditTarget =
  | { kind: "wall"; wid: number }
  | { kind: "opening"; wallId: number; openingId: number; field: OpeningField }
  | { kind: "measure"; chain: number; seg: number; comp: MeasureComp };
// (openingFieldValue / applyOpeningField live in floor/tools/openings.)
let dimEditTarget: DimEditTarget | null = null;
let dimEditPadH = 8; // horizontal padding (px), set per-open to mirror the pill
const _dimEditNdc = new THREE.Vector2();
const _dimEditWorld = new THREE.Vector3();

// ── In-place corner lock (Select tool) ──────────────────────────────────────
// (Markers, hover ring, pickVertexScreen, click-to-lock live in floor/tools/walls.)

// The wall pill is held at a constant on-screen height of BASE_H(0.155) ×
// WALL_LABEL_SCALE(1/6) of the canvas height, drawn on a 64-tall texture with a
// 30px font and 26px side padding. Mirroring those ratios sizes the edit box to
// the pill it replaces (so it reads as the same pill recoloured), at any zoom.
const PILL_SPRITE_FRAC = 0.155 / 6;
function styleDimEditLikePill(): void {
  const h = renderer.domElement.getBoundingClientRect().height;
  const px = (n: number) => PILL_SPRITE_FRAC * (n / 64) * h;
  const fontPx = Math.max(9, Math.min(22, Math.round(px(30))));
  dimEditPadH = Math.max(4, Math.round(px(15))); // matches sprite-label PAD_X
  const padV = Math.max(2, Math.round(px(7)));
  const radius = Math.max(4, Math.round(px(20)));
  dimEdit.style.fontSize = `${fontPx}px`;
  dimEdit.style.padding = `${padV}px ${dimEditPadH}px`;
  dimEdit.style.borderRadius = `${radius}px`;
  dimEditMeasure.style.fontSize = `${fontPx}px`;
}

// Grow/shrink the box to fit its text (+ padding, border, caret slack), with a
// sensible floor so an empty field doesn't collapse.
function autosizeDimEdit(): void {
  dimEditMeasure.textContent = dimEdit.value || dimEdit.placeholder || "0";
  const w = dimEditMeasure.offsetWidth + 2 * dimEditPadH + 2 + 4; // padding+border+caret
  dimEdit.style.width = `${Math.max(40, w)}px`;
}

function positionDimEdit(x: number, y: number, z: number): void {
  _dimEditWorld.set(x, y, z).project(activeCamera());
  const r = renderer.domElement.getBoundingClientRect();
  dimEdit.style.left = `${(_dimEditWorld.x * 0.5 + 0.5) * r.width + r.left}px`;
  dimEdit.style.top = `${(-_dimEditWorld.y * 0.5 + 0.5) * r.height + r.top}px`;
}
// Keep the box pinned over its pill while open (so it follows a pan/zoom).
function syncDimEditPos(): void {
  const t = dimEditTarget;
  if (!t) return;
  if (t.kind === "wall") {
    const li = wallSystem.wallLabelInfo(t.wid);
    if (!li) closeDimEdit();
    else {
      const o = wallLabels.pillOffset(t.wid); // the box stands in for the offset pill
      positionDimEdit(li.x + o.dx, 0.06, li.z + o.dz);
    }
  } else if (t.kind === "measure") {
    const info = measureSystem.componentInfo(t, t.comp);
    if (!info || info.dist <= 1e-4) closeDimEdit(); // leg vanished (undo/delete/axis-aligned)
    else positionDimEdit(info.midX, info.midY + 0.15, info.midZ); // over its leg pill
  } else {
    // The opening must still exist, still be the selected one, and be viewable.
    if (
      !wallSystem.openingProps(t.wallId, t.openingId) ||
      !_sameOpening(openings.selected(), { wallId: t.wallId, openingId: t.openingId }) ||
      !openingsViewable()
    ) {
      closeDimEdit();
      return;
    }
    const p = openings.pillPosition(t.field);
    positionDimEdit(p.x, p.y, p.z);
  }
}
function openDimEditWall(wid: number): void {
  const info = wallSystem.info(wid);
  const li = wallSystem.wallLabelInfo(wid);
  if (!info || !li) return;
  dimEditTarget = { kind: "wall", wid };
  wallLabels.setHiddenWall(wid); // the edit box stands in for this wall's pill
  dimEdit.value = formatLengthIn(info.length * M_TO_IN, getDisplayUnit());
  styleDimEditLikePill(); // match the pill's on-screen size before measuring
  autosizeDimEdit();
  const o = wallLabels.pillOffset(wid); // stand in for the offset pill, not the line
  positionDimEdit(li.x + o.dx, 0.06, li.z + o.dz);
  dimEdit.style.display = "block";
  dimEdit.focus();
  dimEdit.select();
}
function openDimEditOpening(wallId: number, openingId: number, field: OpeningField): void {
  const cur = openings.fieldValue(wallId, openingId, field);
  if (cur === null) return;
  dimEditTarget = { kind: "opening", wallId, openingId, field };
  // The edited pill is hidden by the openings slice while the box stands in.
  dimEdit.value = formatLengthIn(cur * M_TO_IN, getDisplayUnit());
  styleDimEditLikePill();
  autosizeDimEdit();
  const p = openings.pillPosition(field);
  positionDimEdit(p.x, p.y, p.z);
  dimEdit.style.display = "block";
  dimEdit.focus();
  dimEdit.select();
}
// Double-clicking a measurement's component (X-leg or Z-leg) pill edits that
// leg's length in place, like a wall's. Both ends locked → nothing could move,
// so don't open at all.
function openDimEditMeasure(pill: { chain: number; seg: number; comp: MeasureComp }): void {
  const info = measureSystem.componentInfo(pill, pill.comp);
  if (!info || info.dist <= 1e-4 || (info.aLocked && info.bLocked)) return;
  dimEditTarget = { kind: "measure", chain: pill.chain, seg: pill.seg, comp: pill.comp };
  measureSystem.setHiddenSeg(pill); // the edit box stands in for this pill
  dimEdit.value = formatLengthIn(info.dist * M_TO_IN, getDisplayUnit());
  styleDimEditLikePill();
  autosizeDimEdit();
  positionDimEdit(info.midX, info.midY + 0.15, info.midZ);
  dimEdit.style.display = "block";
  dimEdit.focus();
  dimEdit.select();
}
// Apply a typed measurement length. Each unlocked end slides along the
// segment's own line (one end with the other locked; BOTH ends — symmetrically
// about the midpoint — when neither is locked, so two attached models push
// apart equally). An end pinned to a model moves the MODEL (pure translation,
// orientation untouched; refreshAnchors then re-resolves the node) — and
// everything RIGIDLY attached to it: its group, plus any model joined to it by a
// fully-locked measurement (and their groups, transitively). So an object held
// fixed-distance to others drags them all along instead of stretching that
// locked measurement. A free end just moves. Deltas come from one plan over the
// original positions, so the moves apply in any order.
function applyMeasureLength(
  t: { chain: number; seg: number; comp: MeasureComp },
  metres: number,
): void {
  const moves = measureSystem.planResize(t, t.comp, metres);
  if (!moves) return; // both ends locked / same model on both ends / degenerate
  let anchoredMoved = false;
  for (const m of moves) {
    if (m.anchorId !== undefined) {
      placementSystem.translate(expandRigidGroup([m.anchorId]), m.dx, 0, m.dz);
      anchoredMoved = true;
    } else {
      measureSystem.movePointBy(m.chain, m.idx, m.dx, m.dz);
    }
  }
  if (anchoredMoved) {
    measureSystem.refreshAnchors(); // re-resolve every anchored node at once
    placementOutline.refresh(); // a moved model may be the selected one
  }
  measureSystem.setSelected({ chain: t.chain, seg: t.seg }); // keep it selected
  updateMeasureTotalUI();
  commitFloor();
}
function closeDimEdit(): void {
  dimEditTarget = null;
  wallLabels.setHiddenWall(null); // restore the wall's pill
  measureSystem.setHiddenSeg(null); // restore the measurement's pill
  dimEdit.style.display = "none";
}
function commitDimEdit(): void {
  const t = dimEditTarget;
  if (!t) return;
  dimEditTarget = null; // clear first so the blur this hide triggers is a no-op
  wallLabels.setHiddenWall(null); // restore the wall's pill
  measureSystem.setHiddenSeg(null); // restore the measurement's pill
  dimEdit.style.display = "none";
  if (t.kind === "wall") {
    setWallLengthFromText(t.wid, dimEdit.value);
  } else if (t.kind === "measure") {
    const unit = getDisplayUnit();
    const inches = parseLength(dimEdit.value, unit === "in" ? "ft" : unit);
    if (inches !== null && inches > 0) applyMeasureLength(t, inches * 0.0254);
  } else {
    const unit = getDisplayUnit();
    const inches = parseLength(dimEdit.value, unit === "in" ? "ft" : unit);
    // Size fields must be > 0; gaps may legitimately be 0 (flush against a neighbour).
    const isGap = t.field !== "width" && t.field !== "height";
    if (inches !== null && (isGap ? inches >= 0 : inches > 0)) {
      openings.applyField(t.wallId, t.openingId, t.field, inches * 0.0254);
      refreshFloorSelectionUI();
      commitFloor();
    }
  }
}
dimEdit.addEventListener("input", autosizeDimEdit);
dimEdit.addEventListener("keydown", (e) => {
  e.stopPropagation(); // app-level shortcuts ignore inputs anyway; be explicit
  if (e.key === "Enter") commitDimEdit();
  else if (e.key === "Escape") closeDimEdit();
});
dimEdit.addEventListener("blur", () => commitDimEdit());

// A click also queues a press+release, which the floor tools consume from the
// render loop on later frames. When the click fully handles a target (opens an
// editor), swallow the still-pending press+release so they don't re-run the
// press path and re-select whatever sits under the cursor — e.g. selecting the
// wall behind an opening's pill, which would drop the opening's editor. Any
// wall-pill drag left armed by an already-processed press is harmless (the drag
// body is gated on the button still being held, and a fresh press clears it).
function swallowClickPress(): void {
  consumeDesktopPress();
  consumeDesktopRelease();
}

// Tell a click (→ edit the pill) from a press that became a drag (e.g. flipping
// the wall length pill's side, which uses this same 0.012-NDC dead-zone). The
// `click` event fires on release even after a drag, so gate the edit on this.
const _dimClickStart = new THREE.Vector2();
const _dimClickNdc = new THREE.Vector2();
let _dimClickMoved = false;
function eventNdc(e: MouseEvent, out: THREE.Vector2): THREE.Vector2 {
  const r = elementRect(renderer.domElement);
  return out.set(
    ((e.clientX - r.left) / r.width) * 2 - 1,
    -((e.clientY - r.top) / r.height) * 2 + 1,
  );
}
renderer.domElement.addEventListener("pointerdown", (e) => {
  if (e.button !== 0) return; // left button only
  eventNdc(e, _dimClickStart);
  _dimClickMoved = false;
});
renderer.domElement.addEventListener("pointermove", (e) => {
  if ((e.buttons & 1) === 0 || _dimClickMoved) return; // LMB up / already a drag
  if (eventNdc(e, _dimClickNdc).distanceTo(_dimClickStart) > 0.012) _dimClickMoved = true;
});

function handleDimPillClick(e: MouseEvent): void {
  if (uiState.mode !== "floorplan" || floorTool !== "select") return;
  if (_dimClickMoved) return; // a drag (e.g. flipping the wall pill's side), not an edit
  eventNdc(e, _dimEditNdc);
  _floorRay.setFromCamera(_dimEditNdc, activeCamera());
  // A selected door/window's dimension pill (size or gap) → edit it in place.
  const selOpening = openings.selected();
  if (selOpening && openings.editorVisible()) {
    const field = openings.pickPillField(_floorRay);
    if (field) {
      e.preventDefault();
      swallowClickPress();
      openDimEditOpening(selOpening.wallId, selOpening.openingId, field);
      return;
    }
  }
  // A measurement's midpoint pill → edit its length in place. (Endpoint vertices
  // aren't handled here: locking is the wall-corner pattern — single-click to
  // highlight, click again to lock — driven by the press/release pipeline.)
  const mseg = measureSystem.pickLabel(_floorRay, wallFaceOccluderDist());
  if (mseg) {
    e.preventDefault();
    swallowClickPress(); // don't let the press re-select what's underneath
    selectMeasureSeg(mseg);
    openDimEditMeasure(mseg);
    return;
  }
  // A wall's length pill.
  if (!wallLabels.group.visible) return;
  const hits = _floorRay.intersectObjects(wallLabels.group.children, false);
  const lbl = hits.find(
    (h) => h.object.visible && typeof h.object.userData.wallId === "number",
  );
  const wid = lbl?.object.userData.wallId as number | undefined;
  if (typeof wid !== "number") return;
  if (lbl!.distance > wallFaceOccluderDist()) return; // through a wall (side elevation)
  e.preventDefault();
  swallowClickPress();
  selectWall(wid);
  openDimEditWall(wid);
}

// (pressMeasureAt / selectMeasureSeg / deleteSelectedMeasurement live in
// floor/tools/measure as pressSegment / selectSegment / deleteSelectedSegment.)
renderer.domElement.addEventListener("click", handleDimPillClick);

// ── Right-click context menu (on a model) ──────────────────────────────────
// A right-click that ORBITS (3D) or pans should not pop the menu, so track
// whether the RMB moved between press and release. The menu is shown on
// pointerUP (not the contextmenu event, which fires at button-DOWN before any
// drag is registered) so a right-drag never pops it.
let _rmbStartX = 0;
let _rmbStartY = 0;
let _rmbDragged = false;
renderer.domElement.addEventListener("pointerdown", (e) => {
  if (e.button !== 2) return;
  _rmbStartX = e.clientX;
  _rmbStartY = e.clientY;
  _rmbDragged = false;
});
renderer.domElement.addEventListener("pointermove", (e) => {
  if ((e.buttons & 2) === 0) return; // RMB not held
  if (Math.hypot(e.clientX - _rmbStartX, e.clientY - _rmbStartY) > 5) _rmbDragged = true;
});
const _ctxNdc = new THREE.Vector2();
// The placements the open context menu acts on — white-edge highlighted while
// the menu is up (so you can see what it'll affect), cleared when it closes.
let contextMenuTargets: number[] = [];
function handleFloorContextMenu(e: MouseEvent): void {
  if (uiState.mode !== "floorplan" || floorTool !== "select") {
    closeContextMenu();
    return;
  }
  if (_rmbDragged) return; // the right-button was dragged to orbit/pan, not clicked
  // Only models get a menu. Pick the placement under the cursor.
  const r = elementRect(renderer.domElement);
  _ctxNdc.set(
    ((e.clientX - r.left) / r.width) * 2 - 1,
    -((e.clientY - r.top) / r.height) * 2 + 1,
  );
  _floorRay.setFromCamera(_ctxNdc, activeCamera());
  // includeHidden: a hidden model ghosts on hover, so you can right-click it to
  // "Show model" again.
  const hit = placementSystem.pickHit(_floorRay, true);
  // In the side elevation, don't menu a model seen THROUGH a wall/doorway.
  if (!hit || hit.distance > wallFaceOccluderDist()) {
    closeContextMenu();
    return;
  }
  // Right-click does NOT change the selection — no gizmo, no lingering highlight.
  // The menu acts on the CURRENT selection if the clicked model is part of it
  // (so you can group several you've already selected); otherwise on just the
  // clicked model's group.
  const sel = selectedPlacementIds();
  const targets = sel.includes(hit.id) ? sel : placementSystem.groupSiblings(hit.id);
  contextMenuTargets = targets; // highlight them while the menu is open
  const multi = targets.length >= 2;
  const noun = multi ? "models" : "model";
  const items: (ContextMenuItem | null)[] = [
    {
      label: idsDimsShown(targets) ? "Hide dimensions" : "Show dimensions",
      action: () => toggleDims(targets),
    },
    {
      label: idsModelShown(targets) ? `Hide ${noun}` : `Show ${noun}`,
      action: () => toggleModelHidden(targets),
    },
  ];
  if (multi && !idsAreSingleGroup(targets)) {
    items.push(null, { label: "Group", action: () => groupPlacements(targets) });
  }
  if (idsHaveGroup(targets)) {
    if (!multi || idsAreSingleGroup(targets)) items.push(null);
    items.push({ label: "Ungroup", action: () => ungroupPlacements(targets) });
  }
  showContextMenu(e.clientX, e.clientY, items);
}
// Show on right-button RELEASE so the drag flag is accurate; always suppress the
// native browser menu (which fires at button-DOWN, too early to know if it's a
// drag) on the canvas.
renderer.domElement.addEventListener("pointerup", (e) => {
  if (e.button !== 2) return;
  handleFloorContextMenu(e);
});
renderer.domElement.addEventListener("contextmenu", (e) => e.preventDefault());

function deleteFloorSelected(): void {
  const wid = wallSystem.selectedId();
  const pid = placementSystem.selectedId();
  if (wid !== null) wallSystem.removeWall(wid);
  else if (pid !== null) placementSystem.remove(pid);
  refreshFloorSelectionUI();
  commitFloor();
}

// Duplicate the selected placement(s): a fresh clone of each, same orientation/
// scale, nudged over so they're visible, then selected. A duplicated GROUP stays
// a group; a multi-selection duplicates each member. One undo step.
function duplicateFloorSelected(): void {
  const ids = selectedPlacementIds();
  if (ids.length === 0) return;
  const wasGroup = idsAreSingleGroup(ids);
  const copies: number[] = [];
  for (const pid of ids) {
    const info = placementSystem.info(pid);
    const src = placementSystem.groupOf(pid);
    if (!info || !src) continue;
    const m = placedModelCache.get(info.modelId);
    if (!m) continue;
    const id = placeBaked(info.modelId, m, src.position.x + 0.3, src.position.z + 0.3);
    const g = placementSystem.groupOf(id);
    if (g) {
      g.position.y = src.position.y;
      g.quaternion.copy(src.quaternion);
      g.scale.copy(src.scale);
    }
    placementSystem.setDimsOverride([id], placementSystem.dimsOverrideOf(pid));
    copies.push(id);
  }
  if (copies.length === 0) return;
  setFloorTool("select");
  if (copies.length > 1) {
    if (wasGroup) placementSystem.createGroup(copies);
    selectPlacements(copies);
  } else {
    selectPlacement(copies[0]);
  }
  commitFloor();
}

// Duplicate the selected door/window onto its wall and select the copy (so it's
// ready to drag into place). Returns true if it handled the duplicate.
function duplicateSelectedOpening(): boolean {
  const sel = openings.selected();
  if (!sel) return false;
  const newId = wallSystem.duplicateOpening(sel.wallId, sel.openingId);
  if (newId === null) return true;
  openings.setSelected({ wallId: sel.wallId, openingId: newId });
  refreshFloorSelectionUI();
  commitFloor();
  return true;
}

function setFloorRotation(deg: number): void {
  const id = placementSystem.selectedId();
  if (id !== null) placementSystem.setRotationDeg(id, deg);
  commitFloor();
}
function setFloorScale(pct: number): void {
  const id = placementSystem.selectedId();
  if (id !== null) placementSystem.setScale(id, pct / 100);
  commitFloor();
}

// Openings on the selected wall.
function addFloorOpening(kind: OpeningKind): void {
  const id = soleSelectedWall();
  if (id === null) return;
  wallSystem.addOpening(id, kind);
  refreshFloorSelectionUI();
  commitFloor();
}

// Add a placement: a shell-only mesh shows INSTANTLY (no main-thread freeze),
// then the baked asset is fetched/computed off-thread and swapped in. Returns
// the new placement id synchronously.
function placeBaked(modelId: string, model: SavedModel, x: number, z: number): number {
  const id = placementSystem.add(modelId, cloneForModel(model, palette), x, z);
  if (!hasAsset(model)) {
    const busy = beginBusy("Loading model…");
    void ensureAsset(model)
      .then((ok) => {
        if (!ok) return; // legacy/unbakeable or no worker → stays shell-only
        dropClone(modelId);
        placementSystem.rebuildModel(modelId, () => cloneForModel(model, palette));
        placementOutline.refresh();
        wakeRendering();
      })
      .finally(() => busy.done());
  }
  return id;
}

// On-demand AO: when presentation lighting turns on, ensure each placed model's
// bake includes its AO mesh (computed off-thread), then swap the placements.
function onPresentationAO(on: boolean): void {
  if (!on) return;
  const todo = placementSystem
    .placedModelIds()
    .map((id) => placedModelCache.get(id))
    .filter((m): m is SavedModel => !!m && !hasAO(m));
  if (todo.length === 0) return;
  const busy = beginBusy("Computing shading…");
  void Promise.all(
    todo.map((m) =>
      ensureAO(m).then((ok) => {
        if (!ok) return;
        dropClone(m.id);
        placementSystem.rebuildModel(m.id, () => cloneForModel(m, palette));
      }),
    ),
  )
    .then(() => {
      placementOutline.refresh();
      wakeRendering();
    })
    .finally(() => busy.done());
}

// Place a model from the collection at the current view center, then select it.
function placeModel(m: SavedModel): void {
  placedModelCache.set(m.id, m); // for synchronous undo restore
  const t = uiState.floorView === "2d" ? floor2DControls.target : orbitControls.target;
  const id = placeBaked(m.id, m, t.x, t.z);
  setFloorTool("select");
  selectPlacement(id);
  commitFloor();
}

// Place a dragged model where it was dropped on the canvas (the floor point
// under the drop position).
const _dropNdc = new THREE.Vector2();
const _dropHit = new THREE.Vector3();
function placeModelAt(m: SavedModel, clientX: number, clientY: number): void {
  const r = renderer.domElement.getBoundingClientRect();
  _dropNdc.set(
    ((clientX - r.left) / r.width) * 2 - 1,
    -((clientY - r.top) / r.height) * 2 + 1,
  );
  _floorRay.setFromCamera(_dropNdc, activeCamera());
  if (!_floorRay.ray.intersectPlane(_floorPlane, _dropHit)) return;
  placedModelCache.set(m.id, m); // for synchronous undo restore
  const id = placeBaked(m.id, m, _dropHit.x, _dropHit.z);
  setFloorTool("select");
  selectPlacement(id);
  commitFloor();
}

// The Measure tool (gesture, snapping, Clear, total readout + its sidebar
// section) is the floor/tools/measure slice. Destructured under the old names
// so the remaining closure call sites (the Select chain's vertex re-snap, the
// load/undo refreshes) keep working until E4 carves them too.
const {
  clearMeasure: clearFloorMeasure,
  refreshTotal: updateMeasureTotalUI,
  pressVertex: pressMeasureVertexAt,
  dragVertex: dragMeasureVertex,
  isVertexDragging: measureVertexDragging,
  clearVertexDrag: clearMeasureVertexDrag,
  releaseVertex: releaseMeasureVertex,
  cancelVertexGesture: cancelMeasureVertexGesture,
  pressSegment: pressMeasureAt,
  selectSegment: selectMeasureSeg,
  deleteSelectedSegment: deleteSelectedMeasurement,
} = installMeasureTool({
  activeCamera,
  sections: ctx.sections,
  activeTool: () => floorTool,
  isShiftHeld: () => shiftHeld,
  hideDrawLabel: () => ctx.floorUI().hideDrawLabel(),
  commitFloor,
  markerScale: (x, z) => measureMarkerScale(x, z),
  occluderDist: () => wallFaceOccluderDist(),
  closeDimEdit: () => closeDimEdit(),
  editPill: (pill) => {
    selectMeasureSeg(pill);
    openDimEditMeasure(pill);
  },
  clearOtherSelections: () => {
    wallSystem.setSelected(null);
    placementSystem.setSelected(null);
    clearOpeningSelection();
    refreshFloorSelectionUI();
  },
});

// The openings (door/window) domain: selection, hover, and the Select-chain
// gesture hooks. Visuals + dim-edit are still here (they read the accessors).
const openings = installOpeningsDomain({
  activeCamera,
  canvasRect: () => renderer.domElement.getBoundingClientRect(),
  activeTool: () => floorTool,
  pillScale: (p) => dimPillScale(p),
  editingOpeningField: (openingId) =>
    dimEditTarget?.kind === "opening" && dimEditTarget.openingId === openingId
      ? dimEditTarget.field
      : null,
  // Opening plan dims share a wall face with that wall's own dimension chain
  // only when the wall dim is shown (global toggle on, or the wall selected) AND
  // its dimSide is -1 — for the +1 case the wall dim line sits on the far face,
  // so they're already clear. In the shared case push the opening chain out past
  // the wall pill: its outward offset (pillOffset, on-screen-constant so zoom-
  // aware) is collinear with the opening normal, and the pill spans ~±that about
  // (line + offset), so 2× it clears the far edge; subtract the 0.10 m gap the
  // opening base already has over the wall line.
  wallDimPlanPush: (wallId) => {
    const shown = ctx.wallDimsOn() || wallSystem.isSelected(wallId);
    if (!shown || wallSystem.dimSideOf(wallId) !== -1) return 0;
    const off = wallLabels.pillOffset(wallId);
    const d = Math.hypot(off.dx, off.dz);
    return Math.max(0, 2 * d - 0.1);
  },
  dropOtherSelections: () => {
    clearMarquee();
    wallSystem.setSelected(null);
    placementSystem.setSelected(null);
    clearMeasureSelection();
    refreshFloorSelectionUI();
  },
  refreshSelectionUI: () => refreshFloorSelectionUI(),
  commitFloor,
});
const clearOpeningSelection = openings.clearSelection;
const openingsViewable = openings.viewable;
const updateOpeningHover = openings.updateHover;

// The walls domain, part 1: the Wall draw tool + the Select-chain corner
// domain (lock markers, click-to-lock, drag-to-move).
const walls = installWallsDomain({
  activeCamera,
  canvasRect: () => renderer.domElement.getBoundingClientRect(),
  activeTool: () => floorTool,
  isShiftHeld: () => shiftHeld,
  wallsOn: () => ctx.wallsOn(),
  showDrawLabel: (text, x, y) => ctx.floorUI().showDrawLabel(text, x, y),
  hideDrawLabel: () => ctx.floorUI().hideDrawLabel(),
  commitFloor,
  refreshSelectionUI: () => refreshFloorSelectionUI(),
  snapToGrid,
  angleSnapRad: FLOOR_ANGLE,
  axisSnapRad: FLOOR_AXIS_SNAP,
  wallMoveSnapTan: WALL_MOVE_SNAP_TAN,
  selectWall: (id) => selectWall(id),
  occluderDist: () => wallFaceOccluderDist(),
});
const finishDraw = walls.finishDraw;
const selectFloorVertex = walls.selectFloorVertex;

// The marquee domain: selection sets + the rubber-band gesture.
const marquee = installMarquee({
  activeCamera,
  canvasRect: () => renderer.domElement.getBoundingClientRect(),
  occluderDist: () => wallFaceOccluderDist(),
  refreshSelectionUI: () => refreshFloorSelectionUI(),
  commitFloor,
  band: desktopMarqueeRect,
});
const marqueeHasSelection = marquee.hasSelection;
const clearMarquee = marquee.clear;
const deleteMarquee = marquee.deleteSelected;

// The placement-move domain: the transform gizmo, the marquee group's body
// drag, and the footprint snapping both use.
const placements = installPlacementsDomain({
  activeCamera,
  activeTool: () => floorTool,
  isShiftHeld: () => shiftHeld,
  snapToGrid,
  angleSnapRad: FLOOR_ANGLE,
  marquee,
  refreshSelectionUI: () => refreshFloorSelectionUI(),
  occluderDist: () => wallFaceOccluderDist(),
  planViewAxis: () => planViewAxis(),
  freeMoveAxis: () => freeMoveAxis(),
  placementOutline,
  contextHighlightIds: () => (isContextMenuOpen() ? contextMenuTargets : []),
  viewScale: (p) => viewScaleFactor(p),
});
const floorGizmo = placements.floorGizmo;
const floorProtractor = placements.floorProtractor;

// Schematic top-down PNG of the current plan (walls as thick lines, placements
// as filled footprints), for the layouts collection tiles. A flat 2D canvas
// draw — no 3D render/lights/overlay-hiding needed. Null when the plan is empty.
function captureFloorThumbnail(): string | undefined {
  const walls = wallSystem.serialize();
  const boxes = placementSystem
    .ids()
    .map((id) => placementSystem.aabbOf(id))
    .filter((b): b is NonNullable<typeof b> => b !== null);
  if (walls.length === 0 && boxes.length === 0) return undefined;
  let minX = Infinity;
  let maxX = -Infinity;
  let minZ = Infinity;
  let maxZ = -Infinity;
  const ext = (x: number, z: number): void => {
    minX = Math.min(minX, x);
    maxX = Math.max(maxX, x);
    minZ = Math.min(minZ, z);
    maxZ = Math.max(maxZ, z);
  };
  for (const w of walls) {
    ext(w.ax, w.az);
    ext(w.bx, w.bz);
  }
  for (const b of boxes) {
    ext(b.minX, b.minZ);
    ext(b.maxX, b.maxZ);
  }
  const pad = Math.max(maxX - minX, maxZ - minZ) * 0.08 + 0.2;
  minX -= pad;
  maxX += pad;
  minZ -= pad;
  maxZ += pad;
  const S = 192;
  const canvas = document.createElement("canvas");
  canvas.width = S;
  canvas.height = S;
  const ctx = canvas.getContext("2d")!;
  ctx.fillStyle = "#1c1e26";
  ctx.fillRect(0, 0, S, S);
  const bw = maxX - minX || 1;
  const bh = maxZ - minZ || 1;
  const scale = Math.min(S / bw, S / bh) * 0.94;
  const ox = (S - bw * scale) / 2;
  const oz = (S - bh * scale) / 2;
  const tx = (x: number): number => ox + (x - minX) * scale;
  const tz = (z: number): number => oz + (z - minZ) * scale;
  ctx.fillStyle = "rgba(111,160,255,0.5)";
  for (const b of boxes) {
    ctx.fillRect(tx(b.minX), tz(b.minZ), (b.maxX - b.minX) * scale, (b.maxZ - b.minZ) * scale);
  }
  ctx.strokeStyle = "#cfd3df";
  ctx.lineWidth = Math.max(2, WALL_THICKNESS * scale);
  ctx.lineCap = "round";
  for (const w of walls) {
    ctx.beginPath();
    ctx.moveTo(tx(w.ax), tz(w.az));
    ctx.lineTo(tx(w.bx), tz(w.bz));
    ctx.stroke();
  }
  return canvas.toDataURL("image/png");
}

// Re-apply a placement's saved per-model state (dim visibility + grouping) after
// it's been (re)added. Group membership is collected by saved label into
// `buckets`, then resolved by finishPlacementGroups once all are added — so a
// model skipped because its collection entry is gone simply drops out of its
// group instead of shifting everyone's indices.
function applyPlacementMeta(
  sp: SavedPlacement,
  id: number,
  buckets: Map<number, number[]>,
): void {
  // New override wins; otherwise migrate the legacy "hidden" flag to a force-hide.
  const dimsOverride = sp.dimsShow !== undefined ? sp.dimsShow : sp.dimsHidden ? false : undefined;
  if (dimsOverride !== undefined) placementSystem.setDimsOverride([id], dimsOverride);
  if (sp.hidden) placementSystem.setHidden([id], true);
  if (sp.group != null) {
    const arr = buckets.get(sp.group) ?? [];
    arr.push(id);
    buckets.set(sp.group, arr);
  }
}
function finishPlacementGroups(buckets: Map<number, number[]>): void {
  for (const arr of buckets.values()) if (arr.length >= 2) placementSystem.createGroup(arr);
}

// Snapshot the current floor plan (walls + openings + placement references).
function buildCurrentFloorPlan(name: string): SavedFloorPlan {
  const now = Date.now();
  return {
    id: newPlanId(),
    name,
    version: 1,
    walls: wallSystem.serialize(),
    placements: placementSystem.serialize(),
    unit: getDisplayUnit(),
    height: wallSystem.height(),
    measure: measureSystem.serialize(measureAnchorToIndex),
    thumbnail: captureFloorThumbnail(),
    createdAt: now,
    updatedAt: now,
  };
}

// Rebuild the floor plan: walls + openings, then re-bake each placement from
// the collection (skipping models no longer in it).
async function loadFloorPlan(plan: SavedFloorPlan): Promise<void> {
  setDisplayUnit(plan.unit);
  wallSystem.setHeight(plan.height ?? WALL_HEIGHT); // before loadWalls → build at the saved height
  wallSystem.loadWalls(plan.walls);
  placementSystem.clearAll();
  const groupBuckets = new Map<number, number[]>();
  for (const sp of plan.placements) {
    // The collection is the source of truth, but fall back to the in-session
    // cache: a model can be known to this session yet absent from the store
    // when persistence is unavailable (private browsing with IndexedDB
    // blocked), and a store read that THROWS shouldn't strand the whole load.
    const m = (await getModel(sp.modelId).catch(() => undefined)) ??
      placedModelCache.get(sp.modelId);
    if (!m) continue; // model deleted from the collection
    placedModelCache.set(sp.modelId, m); // remember for synchronous undo restore
    const id = placeBaked(sp.modelId, m, sp.x, sp.z);
    const g = placementSystem.groupOf(id);
    if (g) {
      g.position.y = sp.y;
      g.quaternion.set(sp.qx, sp.qy, sp.qz, sp.qw);
    }
    placementSystem.setScale(id, sp.scale);
    applyPlacementMeta(sp, id, groupBuckets);
  }
  finishPlacementGroups(groupBuckets);
  // After placements exist, so anchored measure nodes resolve to their models.
  measureSystem.setChains(plan.measure ?? [], measureAnchorToId);
  setFloorTool("select"); // a loaded layout opens on Select, ready to edit
  refreshFloorSelectionUI();
  updateMeasureTotalUI();
  resetFloorHistory(); // a freshly loaded plan is the new baseline
  ctx.floorSession().setCurrent(plan.id, plan.name, plan.createdAt); // Save now overwrites it
  refreshFloorSaveUI();
  // Open centered on the layout — it may have been drawn off-origin, and should
  // open centered on screen rather than wherever it was originally drawn.
  frameFloorView();
}

// Layout New / Import (Save / Save As live on floorSession). New offers to save
// unsaved work first, then blanks the layout; Import reads a .json, stores it,
// and loads it (loadFloorPlan makes the loaded plan the current save).
async function newLayout(): Promise<void> {
  if ((await ctx.floorSession().confirmLeaveUnsaved()) === "cancel") return;
  resetLayout();
}
// Blank the layout WITHOUT the save prompt: New (above, after its guard) and
// the account-scope swap (main, which guards before the auth action) both
// funnel here.
function resetLayout(): void {
  wallSystem.clearAll();
  wallSystem.setHeight(WALL_HEIGHT); // back to the default room height
  placementSystem.clearAll();
  clearMarquee();
  measureSystem.clear(); // a blank layout has no measurements
  ctx.emitLifecycle("new-layout"); // fresh layout: show walls + models + wall dims
  setFloorTool("wall"); // a blank layout opens on the Wall tool
  refreshFloorSelectionUI();
  updateMeasureTotalUI();
  resetFloorHistory();
  ctx.floorSession().clearCurrent();
  refreshFloorSaveUI();
}
function importLayoutFile(): void {
  pickFile("application/json,.json", async (file) => {
    try {
      const plan = await readPlanFile(file);
      await putPlan(plan);
      await loadFloorPlan(plan);
      ctx.modelDock()?.refresh();
    } catch (e) {
      await showAlert((e as Error).message, { title: "Couldn't import layout" });
    }
  });
}

// ── Layout undo/redo ───────────────────────────────────────────────────────
// Snapshot-based history: the whole layout (walls + placements) is serialized
// to a JSON string after each committed change. Undo/redo just restore a
// snapshot. Placements restore synchronously from an in-memory model cache
// (loadFloorPlan re-bakes from the async collection store, but undo can't wait).
const placedModelCache = new Map<string, SavedModel>();
// The undo/redo stacks + dirty baseline live in floor/history (importable by
// any slice); floor-mode injects the serialize/restore pair below at install.
initFloorHistory({
  snapshot: () => floorSnapshot(),
  restore: (json) => restoreFloorJson(json),
  onChange: () => {
    ctx.floorUI().render();
    refreshFloorSaveUI();
  },
});

// Section strings of the layout the scene currently shows (floorCurrentJson
// split into its four independent sections). restoreFloorJson diffs against
// these so an undo of a one-placement nudge doesn't tear down and rebuild
// every wall, placement, and measure in the layout. Kept in sync at the only
// places the scene and the history baseline are (re)aligned: floorSnapshot
// (commitFloor / reset) and restoreFloorJson (undo / redo).
const _liveSections = { walls: "", placements: "", height: "", measure: "" };

function floorSnapshot(): string {
  _liveSections.walls = JSON.stringify(wallSystem.serialize());
  _liveSections.placements = JSON.stringify(placementSystem.serialize());
  _liveSections.height = JSON.stringify(wallSystem.height());
  _liveSections.measure = JSON.stringify(measureSystem.serialize(measureAnchorToIndex));
  // Assembled by hand, but byte-identical to the JSON.stringify({walls,
  // placements, height, measure}) it replaces (same key order, no spaces).
  return (
    `{"walls":${_liveSections.walls},"placements":${_liveSections.placements},` +
    `"height":${_liveSections.height},"measure":${_liveSections.measure}}`
  );
}
function restoreFloorJson(json: string): void {
  const data = JSON.parse(json) as {
    walls: ReturnType<WallSystem["serialize"]>;
    placements: ReturnType<PlacementSystem["serialize"]>;
    height?: number;
    measure?: SerMeasurePt[][];
  };
  // Re-stringifying the parsed sections reproduces the exact strings
  // floorSnapshot built them from (parse→stringify preserves key order), so
  // string equality tells us which sections the scene already matches.
  const wallsJson = JSON.stringify(data.walls);
  const placementsJson = JSON.stringify(data.placements);
  const measureJson = JSON.stringify(data.measure ?? []);
  const wallsChanged = wallsJson !== _liveSections.walls;
  const placementsChanged = placementsJson !== _liveSections.placements;
  // Anchored measure nodes resolve to placements by index, so a placements
  // rebuild re-ids them and the chains must re-resolve even when unchanged.
  const measureChanged =
    measureJson !== _liveSections.measure || placementsChanged;

  wallSystem.setHeight(data.height ?? WALL_HEIGHT); // no-op when unchanged
  if (wallsChanged) {
    // The rebuild assigns fresh vertex ids, so any in-place corner selection is
    // stale — drop its padlock.
    selectFloorVertex(null);
  }
  // Preserve the selection across a rebuild: walls/placements get fresh ids,
  // but their position in the list is stable for the edits that keep the
  // selection meaningful (length / opening / lock / move), so re-select by index.
  const selWallIdx = wallsChanged ? wallSystem.selectedWallIndex() : -1;
  const selPlaceIdx = placementsChanged ? placementSystem.selectedIndex() : -1;
  if (wallsChanged || placementsChanged) {
    // Rebuilt walls/placements get fresh ids, so any marquee set is now stale.
    marquee.setSelection([], []);
  }
  if (wallsChanged) wallSystem.loadWalls(data.walls);
  if (placementsChanged) {
    placementSystem.clearAll();
    const groupBuckets = new Map<number, number[]>();
    for (const sp of data.placements) {
      const m = placedModelCache.get(sp.modelId);
      if (!m) continue; // model unknown this session — skip (can't re-bake)
      const id = placeBaked(sp.modelId, m, sp.x, sp.z);
      const g = placementSystem.groupOf(id);
      if (g) {
        g.position.y = sp.y;
        g.quaternion.set(sp.qx, sp.qy, sp.qz, sp.qw);
      }
      placementSystem.setScale(id, sp.scale);
      applyPlacementMeta(sp, id, groupBuckets);
    }
    finishPlacementGroups(groupBuckets);
  }
  // Restore measurements AFTER placements exist so anchored nodes resolve to
  // their (freshly-id'd) models by index.
  if (measureChanged) measureSystem.setChains(data.measure ?? [], measureAnchorToId);
  if (selWallIdx >= 0) wallSystem.selectWallByIndex(selWallIdx);
  else if (selPlaceIdx >= 0) placementSystem.selectByIndex(selPlaceIdx);
  // (setSelection above already refreshed the wall highlight.)
  updateMeasureTotalUI(); // measure readout follows the restored chains
  refreshFloorSelectionUI();
  _liveSections.walls = wallsJson;
  _liveSections.placements = placementsJson;
  _liveSections.height = JSON.stringify(data.height ?? WALL_HEIGHT);
  _liveSections.measure = measureJson;
}

// (grabDimPill lives in floor/tools/walls.)

// Doors/windows are viewed/edited looking at the wall face: shown in 3D from any
// angle, and in the 2D plan only from a side elevation (camera level, Y up) — not
// the bird's-eye top-down plan (camera looking down the Y axis).
const _ovDir = new THREE.Vector3();
const _tmpVec3 = new THREE.Vector3();
// True in the flat 2D side-elevation view (camera level), where every wall's
// floor-level length pill collapses onto the same screen line and they pile up.
// We hide wall length dims there; opening width/height dims still show.
function inFloorElevation(): boolean {
  if (uiState.floorView !== "2d") return false;
  activeCamera().getWorldDirection(_ovDir);
  return Math.abs(_ovDir.y) < 0.5;
}
// (Opening handles/border/dims rendering lives in floor/tools/openings.)

// Per-frame floor TOOL handling (after the camera update).
function updateFloorTools(): void {
  const fp = floorPointAtCursor();
  const press = consumeDesktopPress();
  const release = consumeDesktopRelease();
  // Corner lock markers + hover ring (a 2D-plan affordance of visible walls);
  // returns the hovered corner for the Select chain. Owned by the walls slice.
  const hoverVid = walls.updateCornerHover();
  // Measurement-vertex hover: the same gray border on the dot under the cursor,
  // picked with the same forgiving ≈15px proximity as clicks. Shown for the
  // Select tool, and for the Measure tool when NOT mid-measurement (placing the
  // second point of a fresh segment shouldn't tease a grab affordance) — that's
  // exactly when a press there grabs the endpoint to move/lock it.
  const measureHoverActive =
    floorTool === "select" ||
    (floorTool === "measure" && measureSystem.lastPoint() === null);
  measureSystem.setHoveredVertex(
    measureHoverActive && fp && !desktopMouse.camDragging
      ? measureSystem.pickEndpoint(fp.x, fp.z, measureMarkerScale(fp.x, fp.z) * 0.27)
      : null,
  );
  updateOpeningHover(); // which door/window (if any) is hovered → reveals its handles
  openings.updateHandles(); // door/window move/resize circles

  // Dispatch the active tool's per-frame body (registered below; floor/core
  // owns the registry so future floor/tools/ slices can register themselves).
  floorTools.frame(floorTool, { fp, press, release, hoverVid });
}

// ── Floor gesture tools ─────────────────────────────────────────────────────
// Registered per-frame bodies for Wall / Measure / Select. Still closure-bound
// to this install (snapping helpers, drag state, ctx) — E3 lifts them into
// floor/tools/ slices one at a time; the dispatch above won't change.
// (The Wall draw tool registers itself from floor/tools/walls.)
floorTools.register({
  id: "select",
  frame: ({ fp, press, release, hoverVid }) => {
    // Select tool. Pick priority: dimension pill (2D, drag to a side) > wall
    // endpoint handle (drag) > placement (select + drag) > wall (select). Then
    // drag whatever's grabbed.
    wallSystem.hidePreview();
    ctx.floorUI().hideDrawLabel();
    if (press) {
      _floorRay.setFromCamera(desktopMouse.ndc, activeCamera());
      const addSel = desktopMouse.pressShift; // Shift → add to selection
      const subSel = desktopMouse.pressSubtract; // Ctrl/Cmd → remove from selection
      marquee.setNonPass(desktopMouse.pressAlt || marquee.frontLayerOn()); // Alt or the toggle
      placements.clearGestures();
      // A press ON a gizmo handle drives the gizmo (handled below), so don't
      // also start a body-drag of the group there.
      const onGizmo = placements.gizmoUnderCursor();
      if (!onGizmo && !addSel && !subSel && placements.beginGroupDrag()) {
        // Press on a marquee-selected item (no modifier) → drag the whole group
        // in the view plane (floor X/Z aerial; X/Y or Y/Z from a side elevation).
        walls.clearWallGestures();
        openings.clearGesture();
        walls.disarmCorner();
      } else if (addSel || subSel) {
        // Shift/Ctrl press → start an add/remove rubber-band from the current
        // selection (single gizmo/handle selection folds in). A no-drag press
        // toggles just the clicked item (handled on release).
        clearOpeningSelection(); // a door can't join a multi-selection — drop it
        walls.clearWallGestures();
        openings.clearGesture();
        walls.disarmCorner();
        marquee.beginAdditiveBand(addSel ? "add" : "remove", desktopMouse.clientX, desktopMouse.clientY);
      } else {
        // A press outside the selection drops the marquee — but NOT a press on
        // the gizmo, which moves the marquee group (clearing it here would empty
        // gizmoPlaces() below and the press would fall through to a new marquee).
        if (!onGizmo) clearMarquee();
        marquee.setReplaceMode();
        walls.clearWallGestures();
        openings.clearGesture();
        let openingConsumed = false;
        // 0a–0c (resize handle / dim pill / opening body) — the openings domain.
        if (openings.press()) {
          walls.disarmCorner();
          openingConsumed = true;
        }
        if (!walls.pillDragArmed() && !openings.gestureArmed() && !openingConsumed) {
          clearOpeningSelection(); // pressing anything else drops the opening editor
          // 1. A gizmo handle on the selection (single object OR marquee group)
          //    — with the front-object veto (placements slice).
          if (placements.pressGizmo()) {
            walls.disarmCorner();
          } else if (walls.pressCorner(hoverVid)) {
            // Corner press — click toggles its lock, drag moves it (walls slice).
          } else if (pressMeasureVertexAt(fp)) {
            // A measurement endpoint → a plain release toggles its lock, a drag
            // moves it. Checked before the model so a vertex sitting on its anchor
            // model grabs the vertex, not the model.
            walls.disarmCorner();
          } else {
            // Depth-test the placement against the walls: a wall in front of an
            // object occludes it, so you can't select the object through a wall.
            const placeHit = placementSystem.pickHit(_floorRay);
            const wallHits = _floorRay.intersectObjects(wallSystem.wallMeshes(), false);
            const wallDist = wallHits.length > 0 ? wallHits[0].distance : Infinity;
            // In the side elevation, nothing on the far side of a wall/doorway
            // is selectable — the wall face occludes models and walls behind it.
            const barDist = wallFaceOccluderDist();
            walls.disarmCorner();
            if (placeHit && placeHit.distance <= wallDist && placeHit.distance <= barDist) {
              // Select only — a placed model moves only via its gizmo handles. A
              // grouped model selects its whole group.
              selectPlacementOrGroup(placeHit.id);
            } else if (wallHits.length > 0 && wallDist <= barDist) {
              // Select the wall AND arm a drag-to-move of its body. Corner
              // handles are picked above, so they still win near a corner.
              const wid = wallSystem.wallIdForMesh(wallHits[0].object);
              selectWall(wid);
              if (wid !== null && fp) walls.armWallDrag(wid, fp);
            } else if (walls.grabDimPill()) {
              // Nothing on the wall under the cursor — but the floating dimension
              // pill (which sits OUTSIDE the wall) is, so drag it to flip its side.
              // Checked last so the wall/door/corner always win over the
              // annotation where they overlap.
            } else if (pressMeasureAt(fp)) {
              // The measurement LINE (away from a vertex) → select that segment.
              // Backspace deletes it; clicking the pill edits its value.
            } else {
              // Empty space → clear ANY selection (wall or placement, so its
              // gizmo disappears) and begin a rubber-band marquee.
              selectWall(null);
              selectPlacement(null);
              clearMeasureSelection();
              marquee.startBand(desktopMouse.clientX, desktopMouse.clientY);
            }
          }
        }
      }
    }
    if (openings.drag()) {
      // Opening edge-resize / slide-along-wall — handled by the openings domain
      // (claims the frame under the original armed + LMB-held gates).
    } else if (placements.dragGizmo()) {
      // Gizmo translate / free-move / rotate (placements slice).
    } else if (walls.dragWallPill(fp)) {
      // Wall length-pill side-flip drag (walls slice).
    } else if (walls.dragCorner(fp)) {
      // Corner drag — dead-zone, snap, move (walls slice).
    } else if (dragMeasureVertex(fp)) {
      // Measurement-endpoint drag — handled by the measure slice (same else-if
      // priority: it claims the frame only while armed + held + on the floor).
    } else if (walls.dragWallBody(fp)) {
      // Wall-body translate drag (walls slice).
    } else if (placements.dragGroup()) {
      // Marquee group body-drag (placements slice).
    }
    // Rubber-band marquee (its own gesture — none of the item-drags are armed).
    marquee.updateBand();
    if (release) {
      marquee.releaseBand();
      // Coalesce a whole drag (vertex move / wall move / group move / gizmo
      // move-rotate-scale / dim-side) into ONE undo step, committed on release.
      // commitFloor no-ops if nothing actually changed (e.g. a select-click).
      // A plain click (no drag) on a resize handle toggles that edge's lock
      // (yellow ↔ red) — the openings domain owns that tail.
      openings.release();
      const wasDragging =
        walls.cornerDragArmed() ||
        measureVertexDragging() ||
        walls.pillDragArmed() ||
        openings.gestureArmed() ||
        placements.gizmoDragArmed() ||
        walls.bodyDragArmed() ||
        placements.groupDragArmed();
      walls.disarmCorner();
      clearMeasureVertexDrag();
      walls.clearWallGestures();
      openings.clearGesture();
      placements.clearGestures();
      // commitFloor no-ops when nothing actually changed, so a vertex lock click
      // (armed drag but no move) doesn't create an empty undo step. The mark is
      // needed because gizmo/group drags write THREE transforms directly,
      // bypassing the system methods that normally mark the mutation.
      if (wasDragging) {
        markFloorMutation();
        commitFloor();
      }
      // A corner press that never became a drag is a click → toggle its lock
      // (yellow ↔ red). A drag just moved it → no toggle. (Walls slice.)
      walls.releaseCorner();
      // Same click-to-lock for a measurement vertex (the measure slice owns it).
      releaseMeasureVertex();
    }
  },
});

// ── Floor placement gizmo ───────────────────────────────────────────────────
// (The gizmo, group-move, and footprint snapping live in floor/tools/placements;
// expandRigidGroup is shared from floor/core.)

// Group scale that keeps the gizmo a roughly constant on-screen size as the
// user zooms/dollies — so it no longer grows/shrinks with the view. Ortho uses
// the (zoom-scaled) frustum height; perspective uses the camera→gizmo distance
// × the fov spread (the scheme three's TransformControls uses).
// World-units-per-screen-height-ish factor at point `p` for the active camera:
// ortho uses the (zoom-scaled) frustum height; perspective uses distance × fov
// spread. Multiply by a constant to get a constant on-screen world size.
const _vsCamPos = new THREE.Vector3();
function viewScaleFactor(p: THREE.Vector3): number {
  const cam = activeCamera();
  if (cam instanceof THREE.OrthographicCamera) {
    return (cam.top - cam.bottom) / cam.zoom;
  }
  const pc = cam as THREE.PerspectiveCamera;
  pc.getWorldPosition(_vsCamPos);
  const dist = _vsCamPos.distanceTo(p);
  return dist * Math.min((1.9 * Math.tan((Math.PI * pc.fov) / 360)) / pc.zoom, 7);
}


// Constant on-screen scale for the measure markers (so the cursor/dots stay the
// same size as you zoom). 1/16 ≈ keeps their default look at the 2D base zoom.
const MEASURE_MARKER_SCALE = 1 / 16;
const _msScalePt = new THREE.Vector3();
function measureMarkerScale(x: number, z: number): number {
  return viewScaleFactor(_msScalePt.set(x, 0, z)) * MEASURE_MARKER_SCALE;
}

// Constant on-screen size for the wall dimension pills. Bigger constant → bigger
// pills (held the same size at every zoom). The dimension line itself is
// world-anchored (it marks a real span); only the text pill is held constant.
const WALL_LABEL_SCALE = 1 / 6;
const _wlScalePt = new THREE.Vector3();
function wallLabelScale(x: number, z: number): number {
  return viewScaleFactor(_wlScalePt.set(x, 0, z)) * WALL_LABEL_SCALE;
}
// Same constant on-screen pill size, but keyed off the pill's full 3D position
// (the height pills sit above the floor, so their y matters for perspective).
function dimPillScale(p: THREE.Vector3): number {
  return viewScaleFactor(p) * WALL_LABEL_SCALE;
}

// Gap (world metres) the model dimension lines sit outside the footprint.
const MODEL_DIM_GAP = 0.14;
// Build the per-axis model dimension lines: each placed model gets a width line
// (front) and a depth line (right side); in 3D it also gets a vertical height
// line at the front-right corner. Each is an A→B span with end ticks + a pill,
// so it's clear which number is which.
type ModelBounds = {
  minX: number; maxX: number; minY: number; maxY: number; minZ: number; maxZ: number;
};
// Union AABB of several placements (null if none have bounds) — for a group's
// single bounding-box dimension set.
function combinedBounds(ids: Iterable<number>): ModelBounds | null {
  let b: ModelBounds | null = null;
  for (const id of ids) {
    const o = placementSystem.boundsOf(id);
    if (!o) continue;
    if (!b) b = { ...o };
    else {
      b.minX = Math.min(b.minX, o.minX); b.maxX = Math.max(b.maxX, o.maxX);
      b.minY = Math.min(b.minY, o.minY); b.maxY = Math.max(b.maxY, o.maxY);
      b.minZ = Math.min(b.minZ, o.minZ); b.maxZ = Math.max(b.maxZ, o.maxZ);
    }
  }
  return b;
}
// Width (front) + depth (right) + (3D) height dimension spans for one AABB.
function pushBoxDims(
  specs: DimSpec[],
  b: ModelBounds,
  g: number,
  show3d: boolean,
  fmt: (m: number) => string,
): void {
  const yBase = b.minY + 0.02; // footprint lines sit at the model's base
  // Width (X span) in front.
  specs.push({
    a: { x: b.minX, y: yBase, z: b.minZ - g },
    b: { x: b.maxX, y: yBase, z: b.minZ - g },
    tick: DimensionLabels.tickZ(),
    text: fmt(b.maxX - b.minX),
  });
  // Depth (Z span) to the right.
  specs.push({
    a: { x: b.maxX + g, y: yBase, z: b.minZ },
    b: { x: b.maxX + g, y: yBase, z: b.maxZ },
    tick: DimensionLabels.tickX(),
    text: fmt(b.maxZ - b.minZ),
  });
  // Height (Y span) at the front-right corner — 3D only.
  if (show3d) {
    specs.push({
      a: { x: b.maxX + g, y: b.minY, z: b.minZ - g },
      b: { x: b.maxX + g, y: b.maxY, z: b.minZ - g },
      tick: DimensionLabels.tickX(),
      text: fmt(b.maxY - b.minY),
    });
  }
}
function syncExtraDims(): void {
  const fmt = (m: number): string => formatLengthIn(m * M_TO_IN, getDisplayUnit());
  const show3d = uiState.floorView === "3d";
  const g = MODEL_DIM_GAP;
  const specs: DimSpec[] = [];
  // A GROUP reads as one unit: a single bounding-box dimension set covering all
  // its members, instead of each member's own dims.
  const grouped = new Set<number>();
  for (const members of placementSystem.groups()) {
    for (const id of members) grouped.add(id);
    if (!members.some((id) => dimsVisibleOf(id))) continue; // group's dims hidden
    const b = combinedBounds(members);
    if (b) pushBoxDims(specs, b, g, show3d, fmt);
  }
  // Per-model dims for every ungrouped model whose dims are effectively visible.
  for (const id of placementSystem.ids()) {
    if (grouped.has(id) || !dimsVisibleOf(id)) continue;
    const b = placementSystem.boundsOf(id);
    if (b) pushBoxDims(specs, b, g, show3d, fmt);
  }
  extraDims.sync(specs);
}

// Which world axis the 2D plan is currently looking down — the axis pointing at
// the camera (dominant component of camera→target). The plan can be aligned to
// any axis (top, or a side elevation), so the gizmo mask is keyed off this.
function planViewAxis(): 0 | 1 | 2 {
  const dx = Math.abs(orthoCamera.position.x - floor2DControls.target.x);
  const dy = Math.abs(orthoCamera.position.y - floor2DControls.target.y);
  const dz = Math.abs(orthoCamera.position.z - floor2DControls.target.z);
  if (dy >= dx && dy >= dz) return 1;
  return dx >= dz ? 0 : 2;
}

// Plane the center "free move" handle drags in, given as its normal axis:
// perpendicular to the 2D view axis so it matches the two visible translation
// arrows. The 3D view keeps the floor plane (normal = Y).
function freeMoveAxis(): 0 | 1 | 2 {
  return uiState.floorView === "2d" ? planViewAxis() : 1;
}

// (updateFloorGizmo lives in floor/tools/placements as updateGizmo.)

  // Per-frame floor-mode update, called from the render loop while in
  // floor-plan mode. navActive: the corner nav gizmo owns the gesture.
  function updateFloorFrame(navActive: boolean): void {
    if (!navActive) updateFloorTools();
    placements.updateGizmo();
    openings.updateDims();
    openings.updateAllDims(); // "show all dimensions" overlay (door/window sizes)
    // Presentation roof tracks wall edits at most once a frame (no-op unless
    // the lighting rig has a ceiling mode on AND a wall changed).
    wallSystem.updateCeilingIfDirty();
    // See-through walls: fade any wall standing between the camera and the room
    // interior so you can look inside (point-in-room test on the wall's far side).
    // NOT in the walkthrough: the fade test misfires from INSIDE the room (an
    // inner wall face continued past the fragment is still within the
    // centerline footprint), turning the walls you face into 16%-alpha windows
    // onto the sky. Standing in the room wants solid walls anyway.
    // The see-through ceiling roofs any closed room and fades with the same
    // occlusion test as the walls, so it rides the SAME gate: only in the 3D
    // orbit (not the plan/elevation, where a roof would hide the floor plan; not
    // the walkthrough, which uses the presentation roof instead).
    const seeThru = ctx.wallsOn() && uiState.floorView === "3d" && !uiState.walkthroughActive;
    if (seeThru) {
      activeCamera().getWorldPosition(_tmpVec3);
      wallSystem.updateOcclusionFade(_tmpVec3.x, _tmpVec3.y, _tmpVec3.z);
    } else {
      wallSystem.clearOcclusionFade(); // plan / elevation / walkthrough: walls solid
    }
    wallSystem.showSeeThruCeiling(seeThru);
    // Wall length dims read in the plan/3D but collapse + overlap in a flat side
    // elevation, so hide the whole group there (opening dims still show).
    // Otherwise the group stays up and each wall's pill shows when the global
    // "Wall dimensions" toggle is on, that wall is selected, OR the wall tool is
    // active — so a selected wall (or while drawing walls) always reveals its
    // dimension even with the toggle off.
    const wallDimsOn = ctx.wallDimsOn() || floorTool === "wall";
    wallLabels.setVisible(!inFloorElevation());
    wallLabels.setOccluded(uiState.floorView === "3d"); // walls occlude dims in 3D
    wallLabels.sync(
      wallSystem.wallLabelInfos(),
      (id) => wallDimsOn || wallSystem.isSelected(id),
    );
    wallLabels.updateScales(wallLabelScale); // pills stay constant on-screen size
    // Model (placement) dims: which models show is decided per-model inside
    // syncExtraDims (per-model override ?? global toggle). They still collapse
    // onto the floor line in a side elevation, so hide the whole group there.
    const modelDimsShown = !inFloorElevation();
    extraDims.setVisible(modelDimsShown);
    if (modelDimsShown) {
      syncExtraDims();
      extraDims.updateScales(dimPillScale);
    }
    measureSystem.refreshAnchors(); // anchored measurements follow models live
    // Occlude measurements behind meshes (walls/models) wherever the scene reads
    // as real 3D space — the 3D view AND the 2D side ELEVATION (where you look at
    // the walls edge-on). Only the flat top-down plan keeps them as an always-on-
    // top overlay.
    measureSystem.setOccluded(uiState.floorView === "3d" || inFloorElevation());
    measureSystem.updateMarkerScales(measureMarkerScale);
    walls.updateMarkerScale(); // corner markers scale with the drawing
    syncDimEditPos(); // keep an open in-place editor floated over its pill
  }

  // A selected door/window (any of its handles) → delete just that opening.
  // Returns false if no opening was selected (caller falls through to the
  // marquee / wall / placement delete).
  function deleteSelectedOpening(): boolean {
    const sel = openings.selected();
    if (!sel) return false;
    wallSystem.removeOpening(sel.wallId, sel.openingId);
    clearOpeningSelection();
    refreshFloorSelectionUI();
    commitFloor();
    return true;
  }

  // Whether the current opening selection is a door (gates the door-flip key).
  function hasSelectedDoor(): boolean {
    const sel = openings.selected();
    return !!sel && wallSystem.openingProps(sel.wallId, sel.openingId)?.kind === "door";
  }

  // Flip the selected DOOR's hinge jamb (swing=false) or swing face (swing=true).
  // No-op (returns false) unless a door is selected, so the keymap can fall
  // through. Re-lays out the wall and commits one undo step.
  function flipSelectedDoor(swing: boolean): boolean {
    const sel = openings.selected();
    if (!sel) return false;
    const ok = swing
      ? wallSystem.flipDoorSwing(sel.wallId, sel.openingId)
      : wallSystem.flipDoorHinge(sel.wallId, sel.openingId);
    if (!ok) return false;
    refreshFloorSelectionUI();
    commitFloor();
    return true;
  }

  return {
    // Floor systems (scene graph + data), shared with the host app's mode
    // switching, walkthrough, save wiring, and visibility toggles.
    floorScene,
    wallSystem,
    placementSystem,
    measureSystem,
    wallLabels,
    extraDims,
    placementOutline,
    floorGizmo,
    floorProtractor,
    // Tooling API.
    setFloorTool,
    getFloorTool: () => floorTool,
    refreshFloorSelectionUI,
    finishDraw,
    closeDimEdit,
    selectFloorVertex,
    clearMarquee,
    cancelFloorGesture,
    clearOpeningSelection,
    clearFloorSelection,
    clearFloorMeasure,
    marqueeHasSelection,
    deleteMarquee,
    deleteFloorSelected,
    deleteSelectedOpening,
    flipSelectedDoor,
    hasSelectedDoor,
    setAllOpeningDimsVisible: openings.setShowAllDims,
    deleteSelectedMeasurement,
    duplicateSelectedOpening,
    duplicateFloorSelected,
    inFloorElevation,
    // History / persistence.
    floorUndoAction,
    floorRedoAction,
    resetFloorHistory,
    markFloorClean,
    floorDirty,
    buildCurrentFloorPlan,
    loadFloorPlan,
    newLayout,
    resetLayout,
    importLayoutFile,
    cachePlacedModel: (m: SavedModel) => void placedModelCache.set(m.id, m),
    // Selection panel actions.
    setFloorRotation,
    setFloorScale,
    addFloorOpening,
    setRoomHeight,
    updateMeasureTotalUI,
    placeModel,
    placeModelAt,
    onPresentationAO, // on-demand AO bake when presentation lighting turns on
    // Per-frame + shared input state.
    updateFloorFrame,
    frontLayerOn: () => marquee.frontLayerOn(),
    toggleFrontLayer: () => marquee.toggleFrontLayer(),
    isShiftHeld: () => shiftHeld,
    setShiftHeld: (v: boolean) => {
      shiftHeld = v;
    },
  };
}
